import { useState } from 'react';
import {
  useCreateFaqMutation,
  useDeleteFaqMutation,
  useGetFaqsQuery,
  useTestFaqMutation,
  useUpdateFaqMutation,
  type FaqRecord
} from '@/store/configApi';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { LocationPicker } from '@/components/layout/LocationPicker';
import { Button, EmptyState, Panel, SkeletonRows } from '@/components/ui';

/**
 * The answers written here are the only thing the line can say about topics it
 * cannot derive from structured data. The tester alongside them shows exactly
 * which answer a spoken phrase would reach.
 */
export function AnswersPage() {
  const { locationId } = useActiveLocation();
  const dispatch = useAppDispatch();

  const { data, isLoading } = useGetFaqsQuery(locationId, { skip: !locationId });
  const [createFaq, createState] = useCreateFaqMutation();
  const [updateFaq] = useUpdateFaqMutation();
  const [deleteFaq] = useDeleteFaqMutation();
  const [test, testState] = useTestFaqMutation();

  const [phrase, setPhrase] = useState('');
  const [draft, setDraft] = useState({ question: '', phrases: '', answer: '' });

  const add = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) {
      dispatch(pushToast('A question and an answer are both needed.', 'error'));
      return;
    }

    const result = await createFaq({
      locationId,
      canonicalQuestion: draft.question.trim(),
      acceptedPhrases: draft.phrases
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
      answer: draft.answer.trim(),
      enabled: true,
      priority: 5
    });

    if ('error' in result) {
      dispatch(pushToast('Could not save that answer.', 'error'));
      return;
    }
    setDraft({ question: '', phrases: '', answer: '' });
    dispatch(pushToast('Answer saved.', 'success'));
  };

  const remove = async (faq: FaqRecord) => {
    const result = await deleteFaq(faq._id);
    dispatch(
      'error' in result
        ? pushToast('Could not delete that answer.', 'error')
        : pushToast('Answer deleted.', 'success')
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-display">Answers</h1>
        <LocationPicker />
      </div>

      <p className="max-w-2xl text-small text-slate">
        These are the questions your line can answer in your own words. Anything not covered
        here, and not answerable from your hours or menu, goes to your staff instead.
      </p>

      <Panel title="Try a question">
        <div className="px-4 py-4">
          <div className="flex flex-wrap gap-2">
            <input
              value={phrase}
              onChange={(event) => setPhrase(event.target.value)}
              placeholder="do you cater for parties"
              className="min-w-[16rem] flex-1 rounded border border-rule bg-white px-3 py-2 text-small"
              aria-label="Question to test"
            />
            <Button
              loading={testState.isLoading}
              disabled={!phrase || !locationId}
              onClick={() => test({ phrase, locationId })}
            >
              Test question
            </Button>
          </div>

          {testState.data ? (
            testState.data.matched ? (
              <div className="mt-4 rounded bg-jade-soft px-3 py-2.5">
                <p className="text-micro text-jade">
                  Matched with {Math.round((testState.data.confidence ?? 0) * 100)}% confidence.
                  The caller would hear:
                </p>
                <p className="mt-1 text-small text-ink">{testState.data.answer}</p>
              </div>
            ) : (
              <div className="mt-4 rounded bg-amber-soft px-3 py-2.5">
                <p className="text-micro text-amber">No answer matches. The caller would hear:</p>
                <p className="mt-1 text-small text-ink">{testState.data.fallback}</p>
              </div>
            )
          ) : null}
        </div>
      </Panel>

      <Panel title="Add an answer">
        <div className="space-y-3 px-4 py-4">
          <input
            value={draft.question}
            onChange={(event) => setDraft({ ...draft, question: event.target.value })}
            placeholder="Do you have high chairs?"
            className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            aria-label="Question"
          />
          <input
            value={draft.phrases}
            onChange={(event) => setDraft({ ...draft, phrases: event.target.value })}
            placeholder="Other ways callers ask it, separated by commas"
            className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            aria-label="Other phrasings"
          />
          <textarea
            rows={2}
            value={draft.answer}
            onChange={(event) => setDraft({ ...draft, answer: event.target.value })}
            placeholder="Yes, we have high chairs and a change table."
            className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
            aria-label="Answer"
          />
          <Button loading={createState.isLoading} onClick={add}>
            Save answer
          </Button>
        </div>
      </Panel>

      <Panel title="Your answers">
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : (data?.data.length ?? 0) === 0 ? (
          <EmptyState
            headline="No answers yet"
            hint="Add the questions your staff hear most. Each one is a call they will not have to take."
          />
        ) : (
          <ul className="divide-y divide-rule">
            {data?.data.map((faq) => (
              <li key={faq._id} className="px-4 py-3.5">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-small font-medium text-ink">{faq.canonicalQuestion}</p>
                    <p className="mt-0.5 text-small text-slate">{faq.answer}</p>
                    {faq.acceptedPhrases.length > 0 ? (
                      <p className="mt-1 text-micro text-slate">
                        Also matches: {faq.acceptedPhrases.join(', ')}
                      </p>
                    ) : null}
                  </div>

                  <label className="flex shrink-0 items-center gap-2 text-small text-slate">
                    <input
                      type="checkbox"
                      checked={faq.enabled}
                      onChange={(event) =>
                        updateFaq({ id: faq._id, enabled: event.target.checked })
                      }
                      className="h-4 w-4 accent-[#0B7A5A]"
                    />
                    On
                  </label>

                  <button
                    onClick={() => remove(faq)}
                    className="shrink-0 text-small text-slate hover:text-rust"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
